import { Avatar, Card, notification, Spin } from "antd";
import React, { useEffect, useState } from "react";
import LineChart from "./LineChart";
import { socialMediaIcons } from "../../../util/image";
import { useParams } from "react-router-dom";
import useHttp from "../../../hooks/use-http";
import { apiGenerator, convertUTCToLocal } from "../../../util/functions";
import CONSTANTS from "../../../util/constant/CONSTANTS";
import { LoadingOutlined } from "@ant-design/icons";
import CustomDateRange from "../../../component/common/Custom-date-range";
import dayjs from "dayjs";

const chartTitles = {
  followers: "Followers",
  comments: "Comments",
  likes: "Likes",
  views: "Views",
  impressions: "Impressions",
  impressionsUnique: "Unique Impressions",
  viewsUnique: "Unique Views",
};

const Analytics = (props) => {
  const API = useHttp();
  const { teamId } = useParams();
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({});
  const [dates, setDates] = useState({
    startDate: null,
    endDate: null,
  });

  const chartHeader = ({ followersCount, label }) => {
    return (
      <div className="flex flex-col items-start gap-1">
        <div className="font-semibold text-2xl text-[#020817]">{`${label}: ${followersCount}`}</div>
        <div className="font-normal text-base text-[#6b7280]">
          Graph showing the number of {label?.toLowerCase()} over time.
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!teamId || !selectedPlatform) return;
    API.sendRequest(
      apiGenerator(
        CONSTANTS.API.analytics.get,
        {},
        `?teamId=${teamId}&platformType=${selectedPlatform}${
          dates?.startDate && dates?.endDate
            ? `&startDate=${dates.startDate}&endDate=${dates.endDate}`
            : ""
        }`
      ),
      (res) => {
        if (res?.analytics?.length) {
          const chartData = {
            followers: { data: [] },
            comments: { data: [] },
            likes: { data: [] },
            views: { data: [] },
            impressions: { data: [] },
            impressionsUnique: { data: [] },
            viewsUnique: { data: [] },
          };

          // Create a lookup for dailyChangeAnalytics by date
          const dailyChangeMap = {};
          res?.dailyChangeAnalytics?.forEach((d) => {
            const dateKey = convertUTCToLocal(d?.createdAt, "MMMM, D");
            dailyChangeMap[dateKey] = d;
          });

          res?.analytics?.reverse()?.forEach((item) => {
            const date = convertUTCToLocal(item?.createdAt, "MMMM, D");
            const dailyChange = dailyChangeMap[date] || {};

            chartData.followers.data.push({
              date,
              count: item?.followers ?? 0,
              difference: dailyChange?.followers ?? 0,
            });

            chartData.comments.data.push({
              date,
              count: item?.comments ?? 0,
              difference: dailyChange?.comments ?? 0,
            });

            chartData.likes.data.push({
              date,
              count: item?.likes ?? 0,
              difference: dailyChange?.likes ?? 0,
            });

            chartData.views.data.push({
              date,
              count: item?.views ?? 0,
              difference: dailyChange?.views ?? 0,
            });

            chartData.impressions.data.push({
              date,
              count: item?.impressions ?? 0,
              difference: dailyChange?.impressions ?? 0,
            });

            chartData.impressionsUnique.data.push({
              date,
              count: item?.impressionsUnique ?? 0,
              difference: dailyChange?.impressionsUnique ?? 0,
            });

            chartData.viewsUnique.data.push({
              date,
              count: item?.viewsUnique ?? 0,
              difference: dailyChange?.viewsUnique ?? 0,
            });
          });

          setAnalyticsData(chartData);
        } else {
          setAnalyticsData({
            followers: { data: [] },
            comments: { data: [] },
            likes: { data: [] },
            views: { data: [] },
            impressions: { data: [] },
            impressionsUnique: { data: [] },
            viewsUnique: { data: [] },
          });
        }
      },
      {},
      null,
      (err) => {
        setAnalyticsData({
          followers: { data: [] },
          comments: { data: [] },
          likes: { data: [] },
          views: { data: [] },
          impressions: { data: [] },
          impressionsUnique: { data: [] },
          viewsUnique: { data: [] },
        });
        notification.error({
          message: err,
          duration: "3",
        });
      }
    );
  }, [teamId, selectedPlatform, dates]);

  useEffect(() => {
    if (!teamId) return;
    API.sendRequest(
      apiGenerator(CONSTANTS.API.teams.getOne, { teamId }),

      (res) => {
        const filterList = res?.data?.socialAccounts?.filter(
          (item) => !["LINKEDIN", "TWITTER"].includes(item)
        );
        if (filterList?.length) {
          setSelectedPlatform(filterList[0]);
          setConnectedAccounts(filterList);
        }
      }
    );
  }, [teamId]);

  return (
    <>
      <Spin
        spinning={API?.isLoading}
        indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
      >
        <div className="md:mb-[30px] mb-5 flex justify-between items-center flex-wrap gap-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {connectedAccounts?.map((md) => (
              <Avatar
                key={`selectedplatform_${md}`}
                src={socialMediaIcons?.[md]}
                size={44}
                onClick={() => setSelectedPlatform(md)}
                className={`${
                  selectedPlatform === md
                    ? "border-[#a855f7] bg-[#faf5ff]"
                    : "border-[#d9dbdf]"
                } border-[2px] p-2 bg-transparent cursor-pointer`}
              />
            ))}
          </div>
          <CustomDateRange dates={dates} setDates={setDates} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 xxl:grid-cols-3 gap-[20px] md:gap-[30px] min-h-24 w-full">
          {Object.entries(analyticsData).map(([key, { data }]) => (
            <Card
              key={key}
              className="rounded-[30px]"
              styles={{
                body: { padding: 30 },
              }}
            >
              <div className="flex flex-col gap-4">
                {chartHeader({
                  followersCount: data[data?.length - 1]?.count ?? 0,
                  label: chartTitles[key],
                })}
                <LineChart chartData={data} hoverLabel={chartTitles[key]} />
              </div>
            </Card>
          ))}
        </div>
      </Spin>
    </>
  );
};

export default Analytics;
